/**
 * ч.1359 (рішення власника): обернений ланцюг «на підставі» з ГРОШОВОГО документа і з
 * податкової накладної — не лише ПН → реалізація (ч.1352), а повний набір:
 *   надходження банку → реалізація / рахунок покупцю / замовлення покупця;
 *   списання банку → надходження / рахунок постачальника / замовлення постачальнику;
 *   ПН (вихідна) → реалізація / рахунок / замовлення; вхідна ПН → надходження / рахунок
 *   постачальника / замовлення постачальнику; рахунок → замовлення.
 *
 * Редактор нового документа читає `?bank_receipt=` / `?bank_payment=` / `?tax_invoice=` /
 * `?tax_invoice_in=` / `?from_invoice=`, заповнює шапку (і рядки, коли вони є у джерелі), а
 * після створення звʼязує документи. Для банківського документа: якщо він ще НЕ проведений —
 * ставимо його облікове поле («На підставі»: `goods_sale_id` / `goods_receipt_id`,
 * «замовлення»: `customer_order_id` / `supplier_order_id`) — так оплата рознесеться на
 * створений документ при проведенні; проведений не чіпаємо (його рознесення вже в регістрах) —
 * лише ручний звʼязок у «Звʼязаних документах» (Норма #30 п.12: облікова колонка = облікова дія).
 */
import { api as httpApi } from '@/api/client';
import { bankPayments, bankReceipts } from '@/api/bankDocs';

function errText(e: unknown): string {
  const r = (e as { response?: { data?: { detail?: unknown } }; message?: string }) ?? {};
  const d = r.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d) return JSON.stringify(d);
  return r.message ?? String(e);
}

export type BankKind = 'receipt' | 'payment';

export interface BankPrefill {
  organization_id: string;
  contractor_id: string;
  contract_id: string;
  /** Дата оплати (або дата документа) у форматі для полів дати документа. */
  document_date: string;
  amount: string;
  purpose: string;
  posted: boolean;
  document_number: string;
}

/** Шапка з банківського документа — для нового документа обліку. */
export async function bankPrefill(kind: BankKind, bankId: string): Promise<BankPrefill> {
  const d = kind === 'receipt' ? await bankReceipts.get(bankId) : await bankPayments.get(bankId);
  const date = (d.paid_at ?? d.document_date ?? '').slice(0, 10);
  return {
    organization_id: d.organization_id,
    contractor_id: d.contractor_id ?? '',
    contract_id: d.contract_id ?? '',
    document_date: date ? `${date}T12:00` : '',
    amount: String(d.amount ?? ''),
    purpose: d.payment_purpose ?? '',
    posted: !!d.posted,
    document_number: d.document_number,
  };
}

export type BankLinkField = 'goods_sale_id' | 'goods_receipt_id' | 'customer_order_id' | 'supplier_order_id';

/**
 * Звʼязати створений документ з банківським: непроведений банк — обліковим полем
 * (рознесення при проведенні), проведений — ручним звʼязком. Повертає текст помилки або null.
 */
export async function linkBankDoc(opts: {
  kind: BankKind; bankId: string; createdType: string; createdId: string; field: BankLinkField;
}): Promise<string | null> {
  const { kind, bankId, createdType, createdId, field } = opts;
  try {
    const d = kind === 'receipt' ? await bankReceipts.get(bankId) : await bankPayments.get(bankId);
    if (!d.posted) {
      if (kind === 'receipt') await bankReceipts.update(bankId, { [field]: createdId } as never);
      else await bankPayments.update(bankId, { [field]: createdId } as never);
      return null;
    }
  } catch (e) {
    return `Документ створено, але оновити банківський документ не вдалося: ${errText(e)}`;
  }
  return manualLink(createdType, createdId, kind === 'receipt' ? 'doc.bank_receipts' : 'doc.bank_payments', bankId);
}

/** Ручний звʼязок двох документів (рядок у «Звʼязаних документах» з обох боків). */
export async function manualLink(
  documentType: string, documentId: string, linkedType: string, linkedId: string,
): Promise<string | null> {
  try {
    await httpApi.post('/documents/links', {
      document_type: documentType, document_id: documentId, linked_type: linkedType, linked_id: linkedId,
    });
    return null;
  } catch (e: any) {
    if (e?.response?.status === 409) return null;  // уже звʼязані
    return `Документ створено, але звʼязати з джерелом не вдалося: ${errText(e)}`;
  }
}

/** Підказка-коментар для документа, створеного з оплати. */
export function paymentNote(kind: BankKind, p: BankPrefill): string {
  const what = kind === 'receipt' ? 'надходженням' : 'списанням';
  const date = p.document_date ? new Date(p.document_date).toLocaleDateString('uk-UA') : '';
  return `За банківським ${what} № ${p.document_number}${date ? ` від ${date}` : ''} на суму ${p.amount}${p.purpose ? ` (${p.purpose})` : ''}`;
}
